import React from 'react';
import axios from 'axios';
import { Card } from 'reactstrap';
import {
  Button,
  buttonVariants,
  SearchableSelect,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tag,
} from '@jem-open/jem-ui';
import moment from 'moment';
import debounce from 'lodash.debounce';
import Swal from 'sweetalert2';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import { DataTable, sortableHeader } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { Pager } from '../../../components/pager';
import { environment } from '../../../environment/environment';
import FlaggedButton from '../../../components/FlaggedButton';

/* eslint camelcase: "off" */

let cancelToken;

const PROFILE_TYPE_OPTIONS = [
  { value: null, label: 'All profile types' },
  { value: false, label: 'Permanent' },
  { value: true, label: 'Temporary' },
];

class EmployeeData extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      employees: [],
      employers: [],
      employer: null,
      isTemporary: null,
      loading: false,
      page: 1,
      query: '',
      employeesToDelete: [],
    };
  }

  componentDidMount() {
    this.getEmployees();
    this.getEmployers();
  }

  getEmployees = debounce((params) => {
    // Show loading
    this.setState({ loading: true });
    // Check if there are any previous pending requests
    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel('Operation canceled due to new request.');
    }
    // Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    const isTemporaryParam =
      this.state.isTemporary !== null
        ? { is_temporary: this.state.isTemporary }
        : {};

    axios
      .get(`${environment.baseUrl}/employees/`, {
        params: {
          employer: this.state.employer,
          page: this.state.page,
          pagination: true,
          deleted: true,
          ...isTemporaryParam,
          ...params,
        },
        cancelToken: cancelToken.token,
      })
      .then((res) => {
        this.setState({
          employees: res.data.results,
          pages: res.data.pages,
          loading: false,
        });
      });
  }, 500);

  getEmployers() {
    axios.get(`${environment.baseUrl}/employers/`).then((res) => {
      this.setState({
        employers: res.data.filter((emp) => emp.status !== 'deleted'),
      });
    });
  }

  deleteEmployees = (id, name) => {
    Swal.fire({
      title: `Deleting ${name}?`,
      text: `Note: All the Pending Advances will be rejected for the employee.
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete employee!',
    }).then((result) => {
      if (result.isConfirmed) {
        this.setState({ loading: true });
        axios
          .post(`${environment.baseUrl}/employees/delete/`, {
            employees: [id, ...this.state.employeesToDelete],
          })
          .then(() => {
            Swal.fire({
              title: `${name} has been deleted.`,
              icon: 'success',
            });
            this.setState({ employeesToDelete: [] }, () =>
              this.getEmployees()
            );
          })
          .catch((error) => {
            this.setState({ loading: false });
            apiResponseHandler.handleApiError('Error', error);
          });
      }
    });
  };

  onChange = (e) => {
    const { value } = e.target;
    this.setState({ query: value });
    this.search(value);
  };

  onClear = () => {
    this.setState({ query: '' });
    this.search('');
  };

  search = (value) => {
    const isIdNumberSearch = value.includes(',');
    if (isIdNumberSearch) {
      const idNumbers = value
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .join(',');
      if (idNumbers) {
        this.getEmployees({ id_numbers: idNumbers });
      } else {
        this.getEmployees();
      }
    } else {
      this.getEmployees({ search: value });
    }
  };

  compareByDesc(accessor) {
    const getValue =
      typeof accessor === 'function' ? accessor : (item) => item[accessor];
    return function (a, b) {
      if (getValue(a) < getValue(b)) return 1;
      if (getValue(a) > getValue(b)) return -1;
      return 0;
    };
  }

  sortBy(accessor) {
    const arrayCopy = [...this.state.employees];
    const arrInStr = JSON.stringify(arrayCopy);
    arrayCopy.sort(this.compareByAsc(accessor));
    const arrInStr1 = JSON.stringify(arrayCopy);
    if (arrInStr === arrInStr1) {
      arrayCopy.sort(this.compareByDesc(accessor));
    }
    this.setState({ employees: arrayCopy });
  }

  setPage = (page) => {
    this.setState({ page }, () => this.getEmployees());
  };

  handleSort = (accessor) => this.sortBy(accessor);

  compareByAsc(accessor) {
    const getValue =
      typeof accessor === 'function' ? accessor : (item) => item[accessor];
    return function (a, b) {
      if (getValue(a) < getValue(b)) return -1;
      if (getValue(a) > getValue(b)) return 1;
      return 0;
    };
  }

  render() {
    const { employees, employers, loading, page, pages } = this.state;
    const columns = [
      {
        key: 'first_name',
        label: sortableHeader('Name', 'first_name', this.handleSort),
        render: (value, row) => `${row.first_name} ${row.last_name}`,
      },
      {
        key: 'employer',
        label: sortableHeader(
          'Employer',
          (row) => (row.employer ? row.employer.company_name : ''),
          this.handleSort
        ),
        render: (value, row) => row.employer && row.employer.company_name,
      },
      {
        key: 'smartwage_status',
        label: sortableHeader('Status', 'smartwage_status', this.handleSort),
        render: (value, row) => (
          <div className="flex items-center gap-xxs">
            {row.smartwage_status === 'active' && (
              <Tag variant="success">Active</Tag>
            )}
            {row.smartwage_status === 'suspended' && (
              <Tag variant="pending">Suspended</Tag>
            )}
            {row.smartwage_status === 'deleted' && (
              <Tag variant="failed">Deleted</Tag>
            )}
            {row.is_temporary && <Tag variant="processing">Temporary</Tag>}
          </div>
        ),
      },
      {
        key: 'employee_pay_cycle',
        label: sortableHeader(
          'Pay cycle',
          (row) => (row.employee_pay_cycle ? row.employee_pay_cycle.name : ''),
          this.handleSort
        ),
        render: (value, row) =>
          row.employee_pay_cycle ? row.employee_pay_cycle.name : 'No paycycle',
      },
      {
        key: 'fee_value',
        label: sortableHeader('Fee', 'fee_value', this.handleSort),
        render: (value, row) => (
          <>
            {row.fee_type === 'amount' && 'R'}
            {row.fee_value}
            {row.fee_type === 'percent' && '%'}
          </>
        ),
      },
      {
        key: 'employee_no',
        label: sortableHeader('Employee No', 'employee_no', this.handleSort),
      },
      {
        key: 'date_created',
        label: sortableHeader('Date Added', 'date_created', this.handleSort),
        render: (value) => moment(value).format('DD MMM YY HH:mm'),
      },
      {
        key: 'actions',
        label: <span className="normal-case">Actions</span>,
        width: '170px',
        render: (value, row) => (
          <div className="flex items-center gap-xs">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                this.props.history.push(`/admin/employees/${row.id}`)
              }>
              View
            </Button>
            {row.smartwage_status !== 'deleted' && (
              <FlaggedButton
                flag="ADMIN_PORTAL_DELETE_EMPLOYEES_FEATURE_FLAG_TEMP"
                type="button"
                className={buttonVariants({
                  variant: 'destructive',
                  size: 'sm',
                })}
                onClick={() =>
                  this.deleteEmployees(
                    row.id,
                    `${row.first_name} ${row.last_name}`
                  )
                }>
                Delete
              </FlaggedButton>
            )}
          </div>
        ),
      },
    ];
    return (
      <div className="min-h-screen bg-white p-md">
        <div
          className="-mx-md -mt-md mb-md flex flex-wrap items-center justify-between gap-sm px-md py-lg"
          style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
          <div>
            <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
              Employees
            </h1>
            <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
              View and manage employees across all employers.
            </p>
          </div>
        </div>
        <div className="mb-md flex flex-wrap items-center gap-sm">
          <div className="w-72">
            <SearchableSelect
              options={[
                { value: '', label: 'Show all employers' },
                ...(employers || []).map((employer) => ({
                  value: String(employer.id),
                  label: employer.company_name,
                })),
              ]}
              value={
                this.state.employer === null ? '' : String(this.state.employer)
              }
              placeholder="Show all employers"
              searchPlaceholder="Search employers..."
              emptyMessage="No employers found."
              onValueChange={(value) =>
                this.setState({ employer: value === '' ? null : value }, () =>
                  this.getEmployees()
                )
              }
            />
          </div>
          <div className="w-56">
            <Select
              value={
                this.state.isTemporary === null
                  ? 'all'
                  : String(this.state.isTemporary)
              }
              onValueChange={(value) =>
                this.setState(
                  {
                    isTemporary: value === 'all' ? null : value === 'true',
                  },
                  () => this.getEmployees()
                )
              }>
              <SelectTrigger>
                <SelectValue placeholder="All profile types" />
              </SelectTrigger>
              <SelectContent>
                {PROFILE_TYPE_OPTIONS.map((opt) => (
                  <SelectItem
                    key={opt.label}
                    value={opt.value === null ? 'all' : String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[280px] flex-1">
            <SearchInput
              value={this.state.query}
              placeholder="Search employees or enter comma-separated ID numbers"
              onChange={this.onChange}
              onClear={this.onClear}
            />
          </div>
          <Button
            variant="primary"
            onClick={() => this.props.history.push('/admin/employees/add')}>
            Add New
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              this.props.history.push('/admin/employees/add-temporary')
            }>
            Create Temporary
          </Button>
        </div>
        <Card className="shadow border-0">
          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={employees}
              loading={loading}
              emptyState={
                <EmptyState icon="fas fa-users" message="No employees found" />
              }
              skeletonRows={8}
            />
          </div>
        </Card>

        {pages > 1 && (
          <div className="mt-md flex justify-center">
            <Pager
              currentPage={page}
              totalPages={pages}
              onPageChange={this.setPage}
            />
          </div>
        )}
      </div>
    );
  }
}

export default EmployeeData;
